export const VN_ROOMS_CONFIG = { type: "vnRooms", name: "vnRooms" } as const

export const VN_ROOMS_CONFIG_LINK = "settings/vn-rooms-config"

export const PAY_RULES = [
  { key: "under_size", description: "A room pays 0 when its membership peak stayed below the game's smallest squad size." },
  { key: "low_quality_bitrate", description: "A room pays 0 when its mean bitrate is under qualityFloors.meanBitrateMbps." },
  {
    key: "low_quality_missing_chunks",
    description: "A room pays 0 when more than qualityFloors.missingChunksPercent of its chunks are missing.",
  },
  { key: "low_quality_chunk_gap", description: "A room pays 0 when a chunk gap is longer than qualityFloors.maxChunkGapSeconds." },
  { key: "idle_deduction_verified", description: "Measured idle time is deducted from verified (same-match) play." },
  { key: "idle_deduction_not_matched", description: "Measured idle time is deducted from play that did not match." },
  { key: "not_matched_needs_input", description: "Play that did not match pays only with a usable input log." },
  { key: "live_capture_floor", description: "A stay pays 0 when less than liveCaptureFloorPercent of it was captured live." },
  { key: "idle_void", description: "A player's session pays 0 when idle passes sessionVoidIdlePercent." },
  { key: "unsure_pays", description: "Stays whose match check answered unsure are paid." },
  { key: "conduct_slur", description: "Stretches flagged for slurs in chat are not paid." },
] as const

export type PayRule = typeof PAY_RULES[number]["key"]

export const PAY_RULE_KEYS: readonly PayRule[] = PAY_RULES.map(rule => rule.key)

export const isPayRule = (value: unknown): value is PayRule => (
  typeof value === "string" && (PAY_RULE_KEYS as readonly string[]).includes(value)
)

export const PAIRED_RULES: [PayRule, PayRule][] = [
  ["not_matched_needs_input", "idle_deduction_not_matched"],
  ["idle_deduction_not_matched", "not_matched_needs_input"],
  ["low_quality_chunk_gap", "low_quality_missing_chunks"],
]

export const IDLE_RULES: PayRule[] = [
  "idle_deduction_verified",
  "idle_deduction_not_matched",
  "not_matched_needs_input",
  "idle_void",
]

export const REQUIRED_TIMINGS = [
  "heartbeatIntervalSeconds",
  "stationUnconfirmedSeconds",
  "memberHoldSeconds",
  "roomCloseFallbackMinutes",
] as const

export const IDLE_READINGS = ["whole_stretch", "after_threshold"] as const

type Range = {
  path: string[];
  min: number;
  max: number;
  minInclusive: boolean;
  fallback?: number;
}

export type Threshold = Range & { neededBy: PayRule[] }

export const THRESHOLDS: Threshold[] = [
  { path: ["idleThresholdMinutes"], neededBy: IDLE_RULES, min: 1, max: 60, minInclusive: true },
  { path: ["sessionVoidIdlePercent"], neededBy: ["idle_void"], min: 5, max: 100, minInclusive: true, fallback: 30 },
  { path: ["sessionVoidMinRecordedMinutes"], neededBy: ["idle_void"], min: 5, max: 240, minInclusive: true },
  { path: ["qualityFloors", "meanBitrateMbps"], neededBy: ["low_quality_bitrate"], min: 0, max: 6, minInclusive: false },
  { path: ["qualityFloors", "missingChunksPercent"], neededBy: ["low_quality_missing_chunks"], min: 1, max: 100, minInclusive: true },
  { path: ["qualityFloors", "maxChunkGapSeconds"], neededBy: ["low_quality_chunk_gap"], min: 5, max: 3600, minInclusive: true },
  { path: ["liveCaptureFloorPercent"], neededBy: ["live_capture_floor"], min: 0, max: 100, minInclusive: false },
]

export const IDLE_TUNING: Range[] = [
  { path: ["idleHoldCapSeconds"], min: 0, max: 3600, minInclusive: true, fallback: 10 },
  { path: ["gamepadDeadzoneScale"], min: 0, max: 2.5, minInclusive: false, fallback: 1 },
]
